import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Check, Sparkles, Zap, Crown, Star, FileText, Download, Loader2, LogIn, CheckCircle2, MessageCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CREDIT_PACKAGES, ROUTE_PATHS, type CreditPackage } from '@/lib/index';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/Layout';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const packageIcons: Record<CreditPackage['id'], typeof Sparkles> = {
  starter: Sparkles,
  popularan: Zap,
  vip: Crown,
  premium: Star
};

const packageColors: Record<CreditPackage['id'], string> = {
  starter: 'from-blue-500/20 to-cyan-500/20',
  popularan: 'from-purple-500/20 to-pink-500/20',
  vip: 'from-amber-500/20 to-orange-500/20',
  premium: 'from-violet-500/20 to-fuchsia-500/20'
};

const packageBorders: Record<CreditPackage['id'], string> = {
  starter: 'border-blue-500/30',
  popularan: 'border-purple-500/50',
  vip: 'border-amber-500/40',
  premium: 'border-violet-500/40'
};

export default function ForumCreditsPage() {
  const navigate = useNavigate();
  const { isLoggedIn, userEmail, loading: authLoading, logout } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [loading, setLoading] = useState(false);
  const [paymentSlipHTML, setPaymentSlipHTML] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  const [userCredits, setUserCredits] = useState<number | null>(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  
  // Fetch user credits
  useEffect(() => {
    if (isLoggedIn && userEmail && !authLoading) {
      console.log('useEffect triggered - fetching credits for:', userEmail);
      fetchUserCredits();
    } else {
      console.log('useEffect - conditions not met:', { isLoggedIn, userEmail, authLoading });
    }
  }, [isLoggedIn, userEmail, authLoading]);
  
  const fetchUserCredits = async () => {
    setLoadingCredits(true);
    try {
      console.log('Fetching credits for email:', userEmail);
      
      const { data, error } = await supabase
        .from('user_credits_2026_03_28')
        .select('credits_remaining')
        .eq('email', userEmail)
        .maybeSingle();
      
      console.log('Credits query result:', { data, error });
      
      if (error) {
        console.error('Error fetching credits:', error);
        throw error;
      }
      
      const credits = data?.credits_remaining ?? 0;
      console.log('Setting credits to:', credits);
      setUserCredits(credits);
    } catch (error) {
      console.error('Error in fetchUserCredits:', error);
      setUserCredits(0);
    } finally {
      setLoadingCredits(false);
    }
  };
  
  const handleLogin = async () => {
    if (!email || !email.includes('@')) {
      setLoginMessage('Molimo unesite validan email');
      return;
    }

    setLoginLoading(true);
    setLoginMessage('Prijavljivanje...');

    try {
      // Generate consistent password based on email (same email = same password)
      const generatePassword = (email: string) => {
        // Simple hash-like function to generate consistent password from email
        let hash = 0;
        for (let i = 0; i < email.length; i++) {
          hash = ((hash << 5) - hash) + email.charCodeAt(i);
          hash = hash & hash; // Convert to 32bit integer
        }
        return `auto_${Math.abs(hash).toString(36)}_pass`;
      };
      
      const generatedPassword = generatePassword(email);
      
      // Try sign in first
      let { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: generatedPassword,
      });

      // If sign in fails, create account (trigger will auto-confirm email)
      if (error) {
        console.log('Sign in failed, attempting sign up...');
        
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: email,
          password: generatedPassword,
        });

        console.log('SignUp result:', { signUpData, signUpError });

        if (signUpError) {
          console.error('SignUp Error:', signUpError);
          
          // If user already exists with different password
          if (signUpError.message.includes('already registered') || signUpError.message.includes('already been registered')) {
            setLoginMessage('❌ Email već postoji sa drugim passwordom. Koristite drugi email ili kontaktirajte podršku.');
          } else {
            setLoginMessage('❌ Greška: ' + signUpError.message);
          }
          throw signUpError;
        }

        // Check if we got a session (email auto-confirmed by trigger)
        if (signUpData.session) {
          console.log('Got session immediately!');
          setLoginMessage('✅ Prijavljeni ste!');
          setShowLoginDialog(false);
          // Refresh credits after successful login
          setTimeout(() => fetchUserCredits(), 500);
        } else if (signUpData.user && !signUpData.session) {
          console.log('User created but no session, trying to sign in...');
          
          // Wait a bit for trigger to complete
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Try to sign in again (email should be confirmed now)
          const { data: retryData, error: retryError } = await supabase.auth.signInWithPassword({
            email: email,
            password: generatedPassword,
          });

          if (retryError) {
            console.error('Retry sign in error:', retryError);
            setLoginMessage('❌ Greška: ' + retryError.message);
            throw retryError;
          }

          console.log('Retry sign in successful!');
          setLoginMessage('✅ Prijavljeni ste!');
          setShowLoginDialog(false);
          // Refresh credits after successful login
          setTimeout(() => fetchUserCredits(), 500);
        } else {
          setLoginMessage('❌ Nepoznata greška pri registraciji.');
        }
      } else {
        // Sign in successful
        console.log('Sign in successful!');
        setLoginMessage('✅ Prijavljeni ste!');
        setShowLoginDialog(false);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setLoginMessage('❌ Greška: ' + (error.message || 'Nepoznata greška'));
    } finally {
      setLoginLoading(false);
    }
  };


  const handleBankSlipPayment = async (pkg: CreditPackage) => {
    if (!isLoggedIn) {
      setShowLoginDialog(true);
      return;
    }

    setLoading(true);
    try {

      const { data, error } = await supabase.functions.invoke('generate_payment_slip_2026_03_29', {
        body: {
          packageId: pkg.id,
          amount: pkg.price,
          credits: pkg.credits,
          paymentMethod: 'bank_slip',
        },
      });

      if (error) throw error;
      setPaymentSlipHTML(data.html);
      setReference(data.reference);
    } catch (error: any) {
      console.error('Error:', error);
      alert(error.message || 'Greška pri generisanju uplatnice');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = (pkg: CreditPackage) => {
    setSelectedPackage(pkg);
    handleBankSlipPayment(pkg);
  };

  const downloadPaymentSlip = () => {
    if (!paymentSlipHTML) return;
    
    const blob = new Blob([paymentSlipHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uplatnica-${reference}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printPaymentSlip = () => {
    if (!paymentSlipHTML) return;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(paymentSlipHTML);
      printWindow.document.close();
      printWindow.print();
    }
  };


  return (
    <Layout>
      <div className="min-h-screen bg-background pt-[140px]">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-accent/5 pointer-events-none" />
        
        <div className="container mx-auto px-4 py-16 relative">
          {/* Login Button for Non-Logged Users */}
          {!isLoggedIn && (
            <motion.div 
              className="max-w-4xl mx-auto mb-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                <AlertDescription className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <LogIn className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-800 dark:text-blue-200">
                      Već imate nalog? Prijavite se da vidite svoje kredite i postavite pitanje.
                    </span>
                  </div>
                  <Button 
                    onClick={() => setShowLoginDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <LogIn className="mr-2 h-4 w-4" />
                    Prijavi se
                  </Button>
                </AlertDescription>
              </Alert>
            </motion.div>
          )}

          {/* User Status Bar */}
          {isLoggedIn && userEmail && (
            <motion.div 
              className="max-w-4xl mx-auto mb-8"
              {...fadeInUp}
            >
              <Alert className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
                <AlertDescription className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-green-800 dark:text-green-200">
                      Prijavljeni ste kao: <strong>{userEmail}</strong>
                    </span>
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={logout}
                    className="text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900"
                  >
                    Odjavi se
                  </Button>
                </AlertDescription>
              </Alert>
            </motion.div>
          )}

          {/* User Credits Card */}
          {isLoggedIn && userCredits !== null && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-8"
            >
              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                        <Sparkles className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Vaši Krediti</p>
                        <p className="text-3xl font-bold">
                          {loadingCredits ? (
                            <Loader2 className="w-8 h-8 animate-spin inline" />
                          ) : (
                            <>
                              {userCredits}
                              <span className="text-lg text-muted-foreground ml-2">
                                {userCredits === 1 ? 'kredit' : userCredits < 5 ? 'kredita' : 'kredita'}
                              </span>
                            </>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {userCredits > 0 ? 'Možete postaviti pitanje!' : 'Kupite kredite da biste postavili pitanje'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                      {userCredits > 0 && (
                        <Button
                          size="lg"
                          onClick={() => navigate(ROUTE_PATHS.FORUM_ASK)}
                          className="bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                        >
                          <MessageCircle className="mr-2 h-5 w-5" />
                          Postavi Pitanje
                        </Button>
                      )}
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={() => navigate(ROUTE_PATHS.FORUM)}
                        className="border-primary/30"
                      >
                        Pogledaj Forum
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          <motion.div
            className="text-center mb-12"
            {...fadeInUp}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              💳 Kupi Kredite
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Izaberite paket i način plaćanja. 100 RSD = 1 kredit. Krediti važe 30 dana.
            </p>
          </motion.div>

          {/* Payment Method - Bank Slip Only */}
          <motion.div className="max-w-4xl mx-auto mb-12" {...fadeInUp}>
            <Alert>
              <FileText className="w-4 h-4" />
              <AlertDescription>
                <strong>Način plaćanja:</strong> Generišite uplatnicu i uplatite u banci ili pošti. Krediti će biti dodati nakon provere uplate (do 24h).
              </AlertDescription>
            </Alert>
          </motion.div>

          {/* Login Dialog */}
          <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl">
                  <LogIn className="w-6 h-6 text-primary" />
                  Prijava / Registracija
                </DialogTitle>
                <DialogDescription className="text-base">
                  <strong>Već imate nalog?</strong> Unesite email da se prijavite.<br/>
                  <strong>Novi ste?</strong> Automatski ćemo kreirati nalog za vas.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="vas@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                    autoFocus
                  />
                </div>
                
                {loginMessage && (
                  <Alert>
                    <AlertDescription>{loginMessage}</AlertDescription>
                  </Alert>
                )}
                
                <Button
                  onClick={handleLogin}
                  disabled={loginLoading}
                  className="w-full"
                >
                  {loginLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {loginMessage || 'Prijavljivanje...'}
                    </>
                  ) : (
                    'Nastavi'
                  )}
                </Button>
                
                <p className="text-xs text-center text-muted-foreground">
                  Automatski kreiramo nalog ako ne postoji
                </p>
              </div>
            </DialogContent>
          </Dialog>


          {/* Payment Slip Display */}
          {paymentSlipHTML && (
            <motion.div className="max-w-2xl mx-auto mb-12" {...fadeInUp}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-center">Uplatnica Generisana</CardTitle>
                  <CardDescription className="text-center">
                    Odštampajte ili preuzmite uplatnicu
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center gap-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Poziv na broj:</p>
                    <p className="text-lg font-bold font-mono">{reference}</p>
                  </div>
                  <div className="flex gap-4">
                    <Button onClick={printPaymentSlip} variant="outline" className="gap-2">
                      <FileText className="w-4 h-4" />
                      Odštampaj
                    </Button>
                    <Button onClick={downloadPaymentSlip} className="gap-2">
                      <Download className="w-4 h-4" />
                      Preuzmi HTML
                    </Button>
                  </div>
                  <Alert>
                    <AlertDescription className="text-sm">
                      Nakon uplate, krediti će biti dodati u roku od 24h. Dobićete email potvrdu.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Packages */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {CREDIT_PACKAGES.map((pkg) => {
              const Icon = packageIcons[pkg.id];
              return (
                <motion.div key={pkg.id} variants={fadeInUp}>
                  <Card className={`relative overflow-hidden ${packageBorders[pkg.id]} hover:shadow-xl transition-all duration-300 h-full flex flex-col`}>
                    {pkg.popular && (
                      <Badge className="absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-pink-500">
                        Najpopularnije
                      </Badge>
                    )}
                    
                    <div className={`absolute inset-0 bg-gradient-to-br ${packageColors[pkg.id]} opacity-50`} />
                    
                    <CardHeader className="relative">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary-foreground" />
                      </div>
                      <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                      <CardDescription>
                        <span className="text-3xl font-bold text-foreground">{pkg.credits}</span>
                        <span className="text-muted-foreground"> kredita</span>
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="relative flex-1">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Check className="w-5 h-5 text-green-500" />
                          <span className="text-sm">{pkg.credits} pitanja</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-5 h-5 text-green-500" />
                          <span className="text-sm">Važi 30 dana</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-5 h-5 text-green-500" />
                          <span className="text-sm">Odgovor u 24h</span>
                        </div>
                        {pkg.id !== 'starter' && (
                          <div className="flex items-center gap-2">
                            <Check className="w-5 h-5 text-green-500" />
                            <span className="text-sm text-accent font-medium">
                              +{pkg.credits - Math.floor(pkg.price / 100)} bonus
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>

                    <CardFooter className="relative flex-col gap-3">
                      <div className="text-center w-full">
                        <span className="text-3xl font-bold">{pkg.price.toLocaleString('sr-RS')}</span>
                        <span className="text-muted-foreground"> RSD</span>
                      </div>
                      <Button
                        onClick={() => handlePurchase(pkg)}
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Procesiranje...
                          </>
                        ) : (
                          'Generiši Uplatnicu'
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Info Section */}
          <motion.div className="mt-16 max-w-3xl mx-auto" {...fadeInUp}>
            <Card>
              <CardHeader>
                <CardTitle>Kako funkcioniše?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-primary">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Prijavite se</h4>
                    <p className="text-sm text-muted-foreground">
                      Unesite email i dobićete link za prijavu (magic link - bez lozinke).
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-primary">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Izaberite paket i način plaćanja</h4>
                    <p className="text-sm text-muted-foreground">
                      IPS QR (instant) ili uplatnica (do 24h). Bez provizije!
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-primary">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Postavite pitanja</h4>
                    <p className="text-sm text-muted-foreground">
                      Nakon uplate, krediti će biti dodati i možete postaviti pitanja.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}